/**
 * Public activity page — auth-free route at /activity/:token.
 *
 * Flow:
 *   1. Fetch the activity by token (a live invitation of a published version;
 *      the questionnaire's audience setting plays no part since #1199).
 *   2. Email-gate: visitor enters their email; the backend checks it against
 *      the invitation's mode — `assigned`: an assigned participant, an
 *      assigned internal user or the activity creator (owner self-test);
 *      `open`: any address, only against double answers; `email_list`
 *      (links from before #1199): the addresses on the list.
 *   3. On verified, the responder UI renders. Submission re-runs the same
 *      check on the backend so the gate can't be bypassed.
 *
 * Lives outside ProtectedRoute → no JWT required, by design.
 */

import { useEffect, useState } from 'react'
import { useParams } from 'react-router'
import { useTranslation } from 'react-i18next'
import { Loader2, AlertTriangle, Mail, ArrowRight } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { activitiesService } from '@/services/activities-service'
import { ActivityResponder } from '@/components/applications/activities/ActivityResponder'
import { usePageTheme } from '@/hooks/usePageTheme'
import type {
  Activity,
  ActivitySubmissionCreate,
  ActivitySubmission,
} from '@/types/activities'

const SESSION_KEY_PREFIX = 'cdb-activity-session-'

function getOrCreateSessionId(token: string): string {
  const key = `${SESSION_KEY_PREFIX}${token}`
  let id = localStorage.getItem(key)
  if (!id) {
    id = crypto.randomUUID()
    localStorage.setItem(key, id)
  }
  return id
}

export function PublicActivityPage() {
  const { token } = useParams<{ token: string }>()
  const { t } = useTranslation()
  // A survey link is opened by participants, often outside the desktop: it
  // shows in the neutral light theme whatever the OS theme of this browser is.
  usePageTheme('light')
  const [loading, setLoading] = useState(true)
  const [activity, setActivity] = useState<Activity | null>(null)
  const [error, setError] = useState<string | null>(null)

  // Email gate state
  const [email, setEmail] = useState('')
  const [verifying, setVerifying] = useState(false)
  const [verified, setVerified] = useState<{ name: string | null } | null>(null)
  const [verifyError, setVerifyError] = useState<string | null>(null)

  useEffect(() => {
    if (!token) return
    let cancelled = false
    ;(async () => {
      try {
        const a = await activitiesService.getPublicActivity(token)
        if (!cancelled) setActivity(a)
      } catch (e) {
        if (!cancelled) setError(e instanceof Error ? e.message : String(e))
      } finally {
        if (!cancelled) setLoading(false)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [token])

  const handleVerify = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!token || !email.trim()) return
    setVerifying(true)
    setVerifyError(null)
    try {
      const result = await activitiesService.verifyPublicEmail(token, email.trim())
      setVerified({ name: result.name })
    } catch (err) {
      const reason = (err as Error & { reason?: string }).reason
      const key =
        reason === 'already_submitted'
          ? 'activities.publicGate.errorAlreadySubmitted'
          : reason === 'not_assigned'
            ? 'activities.publicGate.errorNotAssigned'
            : 'activities.publicGate.errorGeneric'
      setVerifyError(t(key))
    } finally {
      setVerifying(false)
    }
  }

  const handleSubmit = async (
    payload: ActivitySubmissionCreate,
  ): Promise<ActivitySubmission> => {
    if (!token) throw new Error('Missing token')
    const sessionId = getOrCreateSessionId(token)
    return activitiesService.submitPublic(token, {
      ...payload,
      respondent_session_id: sessionId,
      respondent_email: email.trim(),
      respondent_name: verified?.name ?? payload.respondent_name,
    })
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[var(--window-background,#ffffff)]">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    )
  }
  if (error || !activity) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-3 bg-[var(--window-background,#ffffff)] p-6 text-center">
        <AlertTriangle className="h-12 w-12 text-red-500" />
        <h1 className="text-xl font-semibold">
          {t('activities.publicUnavailableTitle')}
        </h1>
        <p className="text-sm text-[var(--text-secondary)] max-w-md">
          {error ?? t('activities.publicUnavailableHint')}
        </p>
      </div>
    )
  }

  // Email gate — shown until the visitor verifies. Owner of the activity
  // also lands here and uses the same gate (their email matches via the
  // creator-email path on the backend).
  if (!verified) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[var(--window-background,#ffffff)] p-6">
        <form
          onSubmit={handleVerify}
          className="settings-section w-full max-w-md p-6 space-y-5"
        >
          <div className="flex items-start gap-3">
            <div className="rounded-lg bg-[var(--theme-interactive-color)]/10 p-2.5 shrink-0">
              <Mail className="h-5 w-5 text-[var(--theme-interactive-color)]" />
            </div>
            <div className="flex-1 min-w-0">
              <h1 className="text-xl font-semibold text-[var(--text-color)]">
                {activity.title}
              </h1>
              {activity.description && (
                <p className="text-sm text-[var(--text-secondary)] mt-1 whitespace-pre-line">
                  {activity.description}
                </p>
              )}
            </div>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="public_email">
              {t('activities.publicGate.emailLabel')}
            </Label>
            <Input
              id="public_email"
              type="email"
              required
              autoFocus
              value={email}
              onChange={(e) => {
                setEmail(e.target.value)
                if (verifyError) setVerifyError(null)
              }}
              placeholder={t('activities.publicGate.emailPlaceholder')}
            />
            <p className="text-xs text-[var(--text-secondary)]">
              {t('activities.publicGate.emailHint')}
            </p>
          </div>
          {verifyError && (
            <div className="rounded-md border border-red-500/30 bg-red-500/5 p-3 text-sm text-red-600 flex items-start gap-2">
              <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />
              <span>{verifyError}</span>
            </div>
          )}
          <Button type="submit" className="w-full" disabled={verifying || !email.trim()}>
            {verifying ? (
              <Loader2 className="h-4 w-4 animate-spin mr-2" />
            ) : (
              <ArrowRight className="h-4 w-4 mr-2" />
            )}
            {t('activities.publicGate.continue')}
          </Button>
        </form>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-[var(--window-background,#ffffff)] p-6">
      <ActivityResponder
        activity={activity}
        anonymous={false}
        collectRespondentInfo={false}
        onSubmit={handleSubmit}
      />
    </div>
  )
}
